import { Box, Card, CardContent, Chip, Typography } from "@mui/joy";
import { Target, CheckCircle2, Hammer, Pause, CalendarDays, TrendingUp, TrendingDown } from "lucide-react";
import { useProject } from "../../../../hooks/query";
import { useEffect } from "react";

type DashboardCardProps = {
    title: string;
    icon: React.ReactNode;
    value: number | string;
    change?: string;
    timeRange?: "All time" | "Last Month" | "Current Month";
    customColorScheme?: any;
};

function getMonthYear(dateStr: string): string {
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${date.getMonth() + 1}`;
}

function calculateChange(current: number, previous: number): string {
    if (previous === 0 && current === 0) return "0%";
    if (previous === 0) return "+100%";
    const diff = ((current - previous) / previous) * 100;
    return `${diff > 0 ? "+" : ""}${diff.toFixed(1)}%`;
}

export default function ProjectStats() {
    const { useList } = useProject();
    const listProjectSvc = useList();

    useEffect(() => {
        listProjectSvc.mutateAsync().catch(console.error);
    }, []);

    const projects = listProjectSvc.data || [];

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const currentKey = `${currentYear}-${currentMonth}`;
    const lastMonthKey = `${currentMonth === 1 ? currentYear - 1 : currentYear}-${
        currentMonth === 1 ? 12 : currentMonth - 1
    }`;

    const currentMonthProjects = projects.filter((p: any) => getMonthYear(p.CreatedAt) === currentKey);
    const lastMonthProjects = projects.filter((p: any) => getMonthYear(p.CreatedAt) === lastMonthKey);

    const totalCurrent = currentMonthProjects.length;
    const totalLast = lastMonthProjects.length;

    const inProgressCurrent = currentMonthProjects.filter((p: any) => p.Status === "IN-PROGRESS").length;
    const inProgressLast = lastMonthProjects.filter((p: any) => p.Status === "IN-PROGRESS").length;

    const completedCurrent = currentMonthProjects.filter((p: any) => p.Status === "COMPLETED").length;
    const completedLast = lastMonthProjects.filter((p: any) => p.Status === "COMPLETED").length;

    const notStartedCurrent = currentMonthProjects.filter((p: any) => p.Status === "NOT-STARTED").length;
    const notStartedLast = lastMonthProjects.filter((p: any) => p.Status === "NOT-STARTED").length;

    return (
        <Box display={"flex"} flexWrap="wrap" gap={2} mt={3}>
            <DashboardCard
                title="Projects All Time"
                icon={<CalendarDays size={12} />}
                value={projects.length}
                change="+100%"
                customColorScheme={{
                    background: "linear-gradient(30deg, #175536, #2f6e3b)",
                    color: "white !important",
                }}
                timeRange={"All time"}
            />
            {/* <DashboardCard
                title="Total Projects"
                icon={<Target size={12} />}
                value={totalCurrent}
                change={calculateChange(totalCurrent, totalLast)}
                timeRange="Current Month"
            /> */}
            <DashboardCard
                title="In Progress"
                icon={<Hammer size={12} />}
                value={inProgressCurrent}
                change={calculateChange(inProgressCurrent, inProgressLast)}
                timeRange="Current Month"
            />
            {/* <DashboardCard
                title="Completed"
                icon={<CheckCircle2 size={12} />}
                value={completedCurrent}
                change={calculateChange(completedCurrent, completedLast)}
                timeRange="Current Month"
            /> */}
            <DashboardCard
                title="Not Started"
                icon={<Pause size={12} />}
                value={notStartedCurrent}
                change={calculateChange(notStartedCurrent, notStartedLast)}
                timeRange="Current Month"
            />
        </Box>
    );
}

function DashboardCard({ title, icon, value, change, timeRange, customColorScheme }: DashboardCardProps) {
    return (
        <Card sx={{ minWidth: 250, flex: 1, ...customColorScheme }}>
            <CardContent>
                <Box display={"flex"} justifyContent={"space-between"} mb={2}>
                    <Typography startDecorator={icon} level="body-sm" sx={{ fontWeight: 500, color: "inherit" }}>
                        {title}
                    </Typography>
                    {change !== undefined && (
                        <Chip
                            sx={{ borderRadius: 6, border: "1px solid", borderColor: "divider" }}
                            color={change.includes("+") ? "success" : "danger"}
                        >
                            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.2 }}>
                                <Typography
                                    level="body-sm"
                                    color={change.includes("+") ? "success" : "danger"}
                                    sx={{ fontWeight: 500 }}
                                    startDecorator={
                                        change.includes("+") ? <TrendingUp size={14} /> : <TrendingDown size={14} />
                                    }
                                >
                                    {change}
                                </Typography>
                            </Box>
                        </Chip>
                    )}
                </Box>

                <Box display="flex" alignItems="flex-end" gap={2} justifyContent={"space-between"}>
                    <Typography level="h3" sx={{ color: "inherit" }}>
                        {value}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "inherit" }}>
                        {timeRange}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}
